import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { api, API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Route, Navigation, X, Upload, FileSpreadsheet, Check, Search, Trash2, MousePointerSquareDashed, Save, Bookmark, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StopsLayer from "../components/StopsLayer";
import BoxSelect from "../components/BoxSelect";
import FitBounds from "../components/FitBounds";

function startIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="background:#10b981;color:white;border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;font-weight:800;font-family:Outfit,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,0.45);border:3px solid white;font-size:13px">S</div>`,
    iconSize: [34, 34], iconAnchor: [17, 17],
  });
}

// Virtualized list for huge stop counts
function VirtualStopList({ items, selected, onToggle, onDelete, rowHeight = 64 }) {
  const ref = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const viewportH = 340;
  const total = items.length;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 3);
  const end = Math.min(total, start + Math.ceil(viewportH / rowHeight) + 6);
  const visible = items.slice(start, end);
  const paddingTop = start * rowHeight;
  const paddingBottom = (total - end) * rowHeight;

  return (
    <div ref={ref} className="overflow-auto pr-1" style={{ maxHeight: viewportH }}
         onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
      <div style={{ paddingTop, paddingBottom }}>
        {visible.map((j) => {
          const isSel = selected.has(j.id);
          return (
            <div key={j.id}
                 style={{ height: rowHeight - 6 }}
                 className={`w-full p-2.5 rounded-2xl text-sm transition flex items-start gap-2 mb-1.5 group ${isSel ? "bg-primary/15 border border-primary/40" : "bg-secondary/50 hover:bg-secondary"}`}>
              <button onClick={() => onToggle(j)}
                data-testid={`select-job-${j.id}`}
                className="flex-1 flex items-start gap-2 text-left min-w-0">
                <div className={`w-5 h-5 rounded-md shrink-0 mt-0.5 flex items-center justify-center ${isSel ? "bg-primary text-primary-foreground" : "bg-background border border-border"}`}>
                  {isSel && <Check className="w-3 h-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {j.title}
                    {(j.lat == null || j.lng == null) && (
                      <span title="Keine Koordinaten — nicht auf Karte sichtbar"
                            className="inline-flex items-center rounded-full bg-destructive/15 text-destructive text-[9px] font-bold px-1.5 py-0.5">⚠ KEINE KOORD.</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{j.address || "Keine Adresse"}</div>
                </div>
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(j); }}
                      data-testid={`delete-job-${j.id}`}
                      title="Adresse löschen"
                      className="shrink-0 text-destructive/60 hover:text-destructive hover:bg-destructive/15 rounded-full p-1.5 transition">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [start, setStart] = useState({ lat: "52.5200", lng: "13.4050" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [route, setRoute] = useState(null);
  const [filter, setFilter] = useState("");

  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [chosenRows, setChosenRows] = useState(new Set());
  const [assignTo, setAssignTo] = useState("");
  const [importFileName, setImportFileName] = useState("");
  const [autoTour, setAutoTour] = useState(true);
  const [tourNameInput, setTourNameInput] = useState("");
  const [savingImport, setSavingImport] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ done: 0, total: 0 });

  // Tours state
  const [tours, setTours] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tourForm, setTourForm] = useState({ name: "", date: "", assigned_to: "", notes: "" });

  async function loadAll() {
    const [j, u, t] = await Promise.all([
      api.get("/jobs"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/tours").catch(() => ({ data: [] })),
    ]);
    setJobs(j.data);
    setUsers(u.data);
    setTours(t.data);
    return { jobs: j.data, tours: t.data };
  }
  useEffect(() => {
    (async () => {
      const { tours: ts, jobs: js } = await loadAll();
      const tourId = searchParams.get("tour");
      if (tourId) {
        const t = ts.find(x => x.id === tourId);
        if (t) {
          setRoute({ order: t.stops, total_km: t.total_km, total_minutes: t.total_minutes });
          if (t.start && t.start.lat != null && t.start.lng != null) {
            setStart({ lat: String(t.start.lat), lng: String(t.start.lng) });
          }
          const ids = new Set((t.stops || []).map(s => s.id).filter(Boolean));
          setSelectedIds(new Set([...ids].filter(id => js.find(j => j.id === id && j.lat != null && j.lng != null))));
          toast.success(`Tour "${t.name}" geladen`);
          // Clean URL
          const next = new URLSearchParams(searchParams);
          next.delete("tour");
          setSearchParams(next, { replace: true });
        }
      }
    })();
  }, []);

  async function saveTour() {
    if (!route) { toast.error("Bitte zuerst eine Tour berechnen"); return; }
    if (!tourForm.name.trim()) { toast.error("Tour-Name erforderlich"); return; }
    try {
      await api.post("/tours", {
        name: tourForm.name.trim(),
        date: tourForm.date || null,
        assigned_to: tourForm.assigned_to || null,
        notes: tourForm.notes,
        start: { lat: parseFloat(start.lat), lng: parseFloat(start.lng), label: "Start" },
        stops: route.order,
        total_km: route.total_km,
        total_minutes: route.total_minutes,
      });
      toast.success("Tour gespeichert");
      setSaveOpen(false);
      setTourForm({ name: "", date: "", assigned_to: "", notes: "" });
      loadAll();
    } catch (e) { toast.error("Fehler beim Speichern"); }
  }

  function loadTour(t) {
    setRoute({ order: t.stops, total_km: t.total_km, total_minutes: t.total_minutes });
    if (t.start) setStart({ lat: String(t.start.lat), lng: String(t.start.lng) });
    // Select those stops that still exist as jobs
    const ids = new Set(t.stops.map(s => s.id).filter(Boolean));
    setSelectedIds(new Set([...ids].filter(id => mapJobs.find(j => j.id === id))));
    toast.success(`Tour "${t.name}" geladen (${t.total_km} km)`);
  }

  async function deleteTour(t) {
    if (!window.confirm(`Tour "${t.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/tours/${t.id}`);
      toast.success("Tour gelöscht");
      loadAll();
    } catch { toast.error("Fehler"); }
  }

  // All jobs with coords shown on map
  const mapJobs = useMemo(() => jobs.filter(j => j.lat != null && j.lng != null), [jobs]);
  const noCoordsCount = jobs.length - mapJobs.length;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(j => (j.title || "").toLowerCase().includes(q) || (j.address || "").toLowerCase().includes(q));
  }, [jobs, filter]);

  function toggleSelect(j) {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(j.id)) n.delete(j.id); else n.add(j.id);
      return n;
    });
  }
  function selectAll() { setSelectedIds(new Set(filtered.map(j => j.id))); }
  function clearSelection() { setSelectedIds(new Set()); }

  // Build individual stop markers — only those with coords
  // selectedIds is a Set (insertion-ordered) → we can derive selection order 1..N
  const selectionOrder = useMemo(() => {
    const map = new Map();
    let i = 1;
    for (const id of selectedIds) {
      map.set(id, i++);
    }
    return map;
  }, [selectedIds]);

  const stopMarkers = useMemo(() => mapJobs.map(j => ({
    id: j.id, lat: j.lat, lng: j.lng, title: j.title, address: j.address,
    selected: selectedIds.has(j.id),
    order: selectionOrder.get(j.id) || null,
  })), [mapJobs, selectedIds, selectionOrder]);

  const [optimizing, setOptimizing] = useState(false);

  async function optimize() {
    const stops = mapJobs.filter(j => selectedIds.has(j.id))
                     .map(j => ({ id: j.id, lat: j.lat, lng: j.lng, label: j.title }));
    if (stops.length === 0) { toast.error("Mindestens einen Stopp mit Koordinaten auswählen"); return; }
    if (stops.length > 500) { toast.warning(`${stops.length} Stopps — das kann einen Moment dauern`); }
    setOptimizing(true);
    try {
      const { data } = await api.post("/route/optimize", {
        start: { lat: parseFloat(start.lat), lng: parseFloat(start.lng) },
        stops,
      });
      setRoute(data);
      const engineLabel = data.engine === "ors" ? "über echte Straßen" : "als Luftlinie";
      toast.success(`Route berechnet ${engineLabel}: ${data.total_km} km, ${Math.round(data.total_minutes)} Min`);
      // Scroll route result into view
      setTimeout(() => {
        document.querySelector('[data-testid="route-result"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch { toast.error("Optimierung fehlgeschlagen"); }
    finally { setOptimizing(false); }
  }

  // Live elapsed timer during upload
  const [importElapsed, setImportElapsed] = useState(0);
  useEffect(() => {
    if (!importLoading) { setImportElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setImportElapsed(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(id);
  }, [importLoading]);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Client-side size guard (20 MB reasonable)
    if (file.size > 20 * 1024 * 1024) {
      toast.error(`Datei zu groß (${Math.round(file.size/1024/1024)} MB). Max 20 MB.`);
      e.target.value = ""; return;
    }
    setImportLoading(true);
    const startedAt = Date.now();
    const baseName = (file.name || "Import").replace(/\.(xlsx|xls|csv)$/i, "");
    setImportFileName(baseName);
    setTourNameInput(`${baseName} · ${new Date().toLocaleDateString("de-DE")}`);

    async function attempt(skipGeo) {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("ff_token");
      const url = `${API}/import/preview${skipGeo ? "?skip_geocoding=true" : ""}`;
      const res = await fetch(url, { method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` } });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); }
      catch {
        throw new Error(`Server lieferte keine gültige Antwort (${res.status}). Antwort: ${text.slice(0, 120)}`);
      }
      if (!res.ok) throw new Error(data.detail || `Fehler ${res.status}`);
      return data;
    }

    try {
      let data;
      try {
        data = await attempt(false);
      } catch (err1) {
        console.warn("First upload attempt failed, retrying without geocoding:", err1?.message);
        toast.info("Geocoding dauert zu lange — lade ohne Geocoding und versuche es danach separat...");
        data = await attempt(true);
      }
      setPreview(data);
      setChosenRows(new Set(data.rows.map(r => r.row_index)));
      setImportOpen(true);
      const geocoded = data.rows.filter(r => r.geocoded).length;
      const withCoords = data.rows.filter(r => r.lat && r.lng).length;
      const secs = Math.round((Date.now() - startedAt) / 1000);
      toast.success(`${data.count} Zeilen · ${withCoords} mit Koordinaten (${geocoded} automatisch) · ${secs}s`);
      if (withCoords === 0 && data.count > 0) {
        toast.warning("Keine Adresse konnte geocodiert werden. Nach dem Speichern: Button 'Erneut geocodieren' verwenden.");
      }
    } catch (err) {
      console.error("Import error:", err);
      toast.error(err.message || "Import fehlgeschlagen");
    } finally {
      setImportLoading(false);
      e.target.value = "";
    }
  }

  async function saveImport() {
    const rows = preview.rows
      .filter(r => chosenRows.has(r.row_index))
      .map(r => ({ ...r, assigned_to: assignTo || null }));
    if (rows.length === 0) { toast.error("Keine Zeilen ausgewählt"); return; }

    setSavingImport(true);
    setSaveProgress({ done: 0, total: rows.length });

    // Chunked upload for large lists — prevents ingress timeout & keeps UI responsive
    const CHUNK = 300;
    const chunks = [];
    for (let i = 0; i < rows.length; i += CHUNK) chunks.push(rows.slice(i, i + CHUNK));

    let totalCreated = 0;
    let firstTour = null;
    let firstError = null;

    try {
      for (let idx = 0; idx < chunks.length; idx++) {
        const isFirstChunk = idx === 0;
        const isLastChunkAndTourWanted = autoTour && tourNameInput.trim() && isFirstChunk === false;
        // Tour name only on the FIRST chunk to avoid multiple tours; combine into one tour later? Simpler: create ONE tour with the first chunk.
        const payload = { rows: chunks[idx] };
        if (isFirstChunk && autoTour && tourNameInput.trim()) {
          payload.tour_name = tourNameInput.trim();
          payload.tour_assigned_to = assignTo || null;
        }
        try {
          const { data } = await api.post("/import/save", payload);
          totalCreated += data.created || 0;
          if (data.tour && !firstTour) firstTour = data.tour;
          setSaveProgress({ done: Math.min(totalCreated, rows.length), total: rows.length });
        } catch (e) {
          console.error(`Chunk ${idx + 1}/${chunks.length} failed:`, e);
          firstError = e?.response?.data?.detail || e?.message || "Fehler beim Speichern";
          // Continue with next chunks — partial success is better than nothing
        }
      }

      if (firstTour) {
        toast.success(`${totalCreated} Einsätze · Tour "${firstTour.name}" gespeichert`);
      } else if (totalCreated > 0) {
        toast.success(`${totalCreated} Einsätze erstellt`);
      }
      if (firstError && totalCreated < rows.length) {
        toast.error(`Einige Zeilen fehlgeschlagen: ${firstError}`);
      }

      setImportOpen(false);
      setPreview(null);
      setChosenRows(new Set());
      loadAll();
    } finally {
      setSavingImport(false);
      setSaveProgress({ done: 0, total: 0 });
    }
  }

  function toggleRow(idx) {
    setChosenRows(s => { const n = new Set(s); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  }

  async function clearAllStops() {
    const total = jobs.length;
    if (total === 0 && tours.length === 0) {
      toast.info("Es gibt bereits keine Stopps oder Touren");
      return;
    }
    const alsoTours = tours.length > 0
      ? window.confirm(`Sollen auch die ${tours.length} gespeicherten Touren gelöscht werden?\n\nOK = ja (kompletter Reset)\nAbbrechen = nur Stopps löschen, Touren behalten`)
      : false;
    const confirmMsg = alsoTours
      ? `KOMPLETTER RESET:\n\n• ${total} Stopps\n• ${tours.length} Touren\n\nunwiderruflich löschen?`
      : `${total} Stopps auf der Karte löschen?\n\nGespeicherte Touren (${tours.length}) bleiben erhalten.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const { data } = await api.post("/jobs/bulk-delete", { all: true, include_tours: alsoTours });
      const parts = [`${data.deleted} Stopps entfernt`];
      if (data.tours_deleted > 0) parts.push(`${data.tours_deleted} Touren gelöscht`);
      toast.success(parts.join(" · "));
      setSelectedIds(new Set()); setRoute(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  async function deleteOne(m) {
    try {
      await api.delete(`/jobs/${m.id}`);
      toast.success("Stopp gelöscht");
      setSelectedIds(prev => { const n = new Set(prev); n.delete(m.id); return n; });
      setJobs(prev => prev.filter(j => j.id !== m.id));
    } catch { toast.error("Löschen fehlgeschlagen"); }
  }

  async function deleteSelected() {
    if (selectedIds.size === 0) { toast.error("Keine Stopps ausgewählt"); return; }
    if (!window.confirm(`${selectedIds.size} ausgewählte Stopps löschen?`)) return;
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/jobs/bulk-delete", { ids });
      toast.success(`${data.deleted} Stopps gelöscht`);
      setSelectedIds(new Set());
      setRoute(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  function handleBoxSelect(ids, append) {
    setSelectedIds(prev => {
      const n = append ? new Set(prev) : new Set();
      for (const id of ids) n.add(id);
      return n;
    });
    if (ids.length > 0) toast.success(`${ids.length} Stopps ausgewählt`);
  }

  const center = mapJobs[0] ? [mapJobs[0].lat, mapJobs[0].lng] : [52.52, 13.405];
  const polyline = route
    ? (route.polyline && route.polyline.length > 0
        ? route.polyline
        : [[parseFloat(start.lat), parseFloat(start.lng)], ...route.order.map(o => [o.lat, o.lng])])
    : null;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="map-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Navigation</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Karte & Routen</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-semibold text-foreground">{mapJobs.length}</span> von {jobs.length} Stopps auf der Karte sichtbar
            {noCoordsCount > 0 && <span className="text-destructive ml-2">· {noCoordsCount} ohne Koordinaten (nicht sichtbar)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIds.size > 0 && (
            <Button variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={deleteSelected} data-testid="btn-delete-selected">
              <Trash2 className="w-4 h-4 mr-1" /> {selectedIds.size} löschen
            </Button>
          )}
          {jobs.length > 0 && (
            <Button variant="outline" className="rounded-full" onClick={clearAllStops} data-testid="btn-clear-all">
              <Trash2 className="w-4 h-4 mr-1" /> Alle löschen
            </Button>
          )}
          <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground cursor-pointer font-semibold text-sm transition"
                 data-testid="btn-import-excel">
            {importLoading
              ? (<><div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" /> Adressen werden geocodiert... {importElapsed}s</>)
              : (<><FileSpreadsheet className="w-4 h-4" /> Excel/CSV importieren</>)}
            <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} disabled={importLoading} data-testid="input-file" />
          </label>
        </div>
      </div>

      {noCoordsCount > 0 && (
        <Card className="rounded-3xl p-4 border-destructive/30 bg-destructive/10 flex items-center justify-between gap-4 flex-wrap" data-testid="no-coords-warning">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-destructive/20 text-destructive flex items-center justify-center shrink-0">
              ⚠
            </div>
            <div>
              <div className="font-semibold">{noCoordsCount} Adresse{noCoordsCount !== 1 ? "n" : ""} ohne Koordinaten</div>
              <div className="text-xs text-muted-foreground">Diese können nicht auf der Karte angezeigt werden. Geocoding erneut versuchen?</div>
            </div>
          </div>
          <Button size="sm" className="rounded-full" onClick={async ()=>{
            toast.info("Starte Geocoding... das kann bis zu 2 Minuten dauern");
            try {
              const { data } = await api.post("/jobs/geocode-missing");
              toast.success(`${data.updated} von ${data.total} Adressen erfolgreich geocodiert`);
              loadAll();
            } catch (e) { toast.error("Fehler"); }
          }} data-testid="btn-retry-geocode">
            Erneut geocodieren
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-3xl p-6 border-border/60 glass space-y-4 lg:col-span-1" data-testid="route-panel">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Route className="w-5 h-5 text-primary" /> Routenoptimierung</h3>
          <div>
            <label className="text-sm font-medium">Startpunkt</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input value={start.lat} onChange={e=>setStart({...start, lat:e.target.value})} placeholder="Lat" className="rounded-full" data-testid="start-lat" />
              <Input value={start.lng} onChange={e=>setStart({...start, lng:e.target.value})} placeholder="Lng" className="rounded-full" data-testid="start-lng" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Tipp: Berlin ist 52.52, 13.40</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Alle Stopps ({selectedIds.size} von {filtered.length} ausgewählt)</label>
              <div className="flex gap-2">
                <button className="text-xs text-primary hover:underline" onClick={selectAll}>Alle</button>
                <button className="text-xs text-muted-foreground hover:underline" onClick={clearSelection}>Keine</button>
              </div>
            </div>
            <div className="relative mt-2 mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Stopps suchen..." value={filter} onChange={e=>setFilter(e.target.value)}
                     className="rounded-full pl-9 h-9" data-testid="stop-search" />
            </div>
            {filtered.length === 0
              ? <div className="text-sm text-muted-foreground py-4 text-center">Keine passenden Stopps.</div>
              : <VirtualStopList items={filtered} selected={selectedIds} onToggle={toggleSelect} onDelete={deleteOne} />}
          </div>

          <Button onClick={optimize} className="w-full rounded-full" data-testid="btn-optimize"
                  disabled={selectedIds.size === 0 || optimizing}>
            {optimizing
              ? (<><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin mr-2" /> Route wird berechnet...</>)
              : (<><Navigation className="w-4 h-4 mr-2" /> Route erstellen ({selectedIds.size})</>)}
          </Button>

          {route && (
            <div className="rounded-2xl bg-secondary/60 p-4 space-y-3" data-testid="route-result">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm flex items-center gap-1.5">
                  <Route className="w-4 h-4 text-primary" /> Beste Route
                  {route.engine === "ors" && (
                    <span className="rounded-full bg-accent/15 text-accent text-[9px] font-bold px-1.5 py-0.5 ml-1">Echte Straße</span>
                  )}
                  {route.engine === "haversine" && (
                    <span className="rounded-full bg-muted text-muted-foreground text-[9px] font-bold px-1.5 py-0.5 ml-1">Luftlinie</span>
                  )}
                </h4>
                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" onClick={()=>setRoute(null)}><X className="w-3 h-3" /></Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-background/60 p-2 text-center">
                  <div className="font-heading font-bold text-base text-primary">{route.order.length}</div>
                  <div className="text-[10px] text-muted-foreground">Stopps</div>
                </div>
                <div className="rounded-xl bg-background/60 p-2 text-center">
                  <div className="font-heading font-bold text-base">{route.total_km}</div>
                  <div className="text-[10px] text-muted-foreground">km</div>
                </div>
                <div className="rounded-xl bg-background/60 p-2 text-center">
                  <div className="font-heading font-bold text-base">{Math.round(route.total_minutes)}</div>
                  <div className="text-[10px] text-muted-foreground">Min</div>
                </div>
              </div>

              <Button onClick={()=>setSaveOpen(true)} className="w-full rounded-full" size="sm" data-testid="btn-save-tour">
                <Save className="w-4 h-4 mr-2" /> Tour speichern
              </Button>

              <div className="mt-2">
                <div className="text-xs text-muted-foreground mb-1 font-semibold">Route-Details:</div>
                <ol className="space-y-1 max-h-60 overflow-auto">
                  {route.order.slice(0, 50).map((o, i) => (
                    <li key={o.id || i} className="flex items-start gap-2 text-xs bg-background/40 rounded-xl p-2">
                      <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold shrink-0">{i+1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{o.label || o.title}</div>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                          <span>+{o.distance_from_prev_km?.toFixed(1) || "0.0"} km</span>
                          <span>·</span>
                          <span>{o.cumulative_km?.toFixed(1) || "0.0"} km total</span>
                        </div>
                      </div>
                    </li>
                  ))}
                  {route.order.length > 50 && <li className="text-muted-foreground text-xs px-2">… und {route.order.length - 50} weitere Stopps</li>}
                </ol>
              </div>
            </div>
          )}

          {/* Saved Tours */}
          {tours.length > 0 && (
            <div className="rounded-2xl bg-secondary/60 p-4 space-y-3" data-testid="saved-tours">
              <div className="flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-accent" />
                <h4 className="font-semibold text-sm">Gespeicherte Touren ({tours.length})</h4>
              </div>
              <div className="space-y-1.5 max-h-60 overflow-auto">
                {tours.map(t => (
                  <div key={t.id} className="rounded-2xl bg-background/50 hover:bg-background/80 transition p-3 flex items-center gap-2" data-testid={`tour-${t.id}`}>
                    <button onClick={()=>loadTour(t)} className="flex-1 text-left min-w-0">
                      <div className="font-medium text-sm truncate">{t.name}</div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5 flex-wrap">
                        <Badge className="rounded-full bg-primary/15 text-primary text-[9px] px-1.5 py-0">{(t.stops||[]).length} Stopps</Badge>
                        <span>{t.total_km?.toFixed?.(1) || 0} km</span>
                        <span>·</span>
                        <span>{Math.round(t.total_minutes || 0)} Min</span>
                        {t.date && <span>· {new Date(t.date).toLocaleDateString('de-DE')}</span>}
                        {t.assigned_to && users.find(u=>u.id===t.assigned_to) && (
                          <Badge variant="secondary" className="rounded-full text-[9px] px-1.5 py-0">{users.find(u=>u.id===t.assigned_to).name}</Badge>
                        )}
                      </div>
                    </button>
                    <button onClick={()=>deleteTour(t)} className="shrink-0 text-destructive/60 hover:text-destructive rounded-full p-1.5 transition" title="Tour löschen" data-testid={`delete-tour-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="rounded-3xl p-0 border-border/60 overflow-hidden lg:col-span-2" data-testid="map-container">
          <div className={`px-6 py-3 border-b border-border/40 flex items-center gap-2 text-xs flex-wrap transition
                          ${selectedIds.size === 0 ? "text-muted-foreground" : "bg-primary/10 text-foreground"}`}>
            <MousePointerSquareDashed className={`w-4 h-4 shrink-0 ${selectedIds.size === 0 ? "text-accent" : "text-primary"}`} />
            {selectedIds.size === 0 ? (
              <span>
                <b className="text-foreground">Schritt 1:</b> Stopps anklicken zum Markieren ·
                <b className="text-foreground"> Shift + Ziehen</b> f&uuml;r mehrere ·
                <b className="text-foreground"> Rechtsklick</b> zum L&ouml;schen
              </span>
            ) : (
              <span>
                <b>{selectedIds.size}</b> {selectedIds.size === 1 ? "Stopp" : "Stopps"} markiert &middot;
                <b className="text-primary"> Schritt 2:</b> Unten &bdquo;Route zum Befahren erstellen&ldquo; klicken
              </span>
            )}
          </div>
          <div className="h-[600px]">
            <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} preferCanvas>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              <Marker position={[parseFloat(start.lat) || 0, parseFloat(start.lng) || 0]} icon={startIcon()}>
                <Popup><b>Start</b></Popup>
              </Marker>

              <StopsLayer markers={stopMarkers} onToggleSelect={(m) => toggleSelect({ id: m.id })} onDelete={deleteOne} />
              <BoxSelect stops={mapJobs} onBoxSelect={handleBoxSelect} />
              <FitBounds points={mapJobs} />

              {polyline && <Polyline positions={polyline} pathOptions={{ color: "#2563eb", weight: 3.5, opacity: 0.85 }} />}
            </MapContainer>
          </div>
        </Card>
      </div>

      {/* Floating Action Bar: appears when stops are marked */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 animate-in slide-in-from-bottom-4 duration-300"
             data-testid="route-action-bar">
          <div className="rounded-full glass border border-primary/40 shadow-2xl shadow-primary/20 pl-5 pr-2 py-2 flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                {selectedIds.size}
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight">
                  {selectedIds.size === 1 ? "1 Stopp" : `${selectedIds.size} Stopps`} markiert
                </div>
                <div className="text-[10px] text-muted-foreground leading-tight">Bereit für die Route</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="rounded-full text-xs h-9"
                    onClick={clearSelection} data-testid="fab-clear">
              Zurücksetzen
            </Button>
            <Button className="rounded-full h-10 px-5 font-semibold"
                    onClick={optimize} disabled={optimizing}
                    data-testid="fab-build-route">
              {optimizing
                ? (<><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin mr-2" /> Berechne...</>)
                : (<><Navigation className="w-4 h-4 mr-2" /> Route zum Befahren erstellen</>)}
            </Button>
          </div>
        </div>
      )}

      {/* Save Tour Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Save className="w-5 h-5 text-accent" /> Tour speichern</DialogTitle></DialogHeader>
          {route && (
            <div className="rounded-2xl bg-secondary/60 p-4 grid grid-cols-3 gap-2 mb-2">
              <div className="text-center"><div className="font-heading font-bold text-primary">{route.order.length}</div><div className="text-[10px] text-muted-foreground">Stopps</div></div>
              <div className="text-center"><div className="font-heading font-bold">{route.total_km}</div><div className="text-[10px] text-muted-foreground">km</div></div>
              <div className="text-center"><div className="font-heading font-bold">{Math.round(route.total_minutes)}</div><div className="text-[10px] text-muted-foreground">Min</div></div>
            </div>
          )}
          <div className="space-y-3">
            <div><Label>Tour-Name *</Label>
              <Input value={tourForm.name} onChange={e=>setTourForm({...tourForm, name:e.target.value})}
                     placeholder="z.B. Berlin Montag Tech-1"
                     className="rounded-full h-10 px-4 mt-1" data-testid="tour-name" autoFocus /></div>
            <div><Label>Datum</Label>
              <Input type="date" value={tourForm.date} onChange={e=>setTourForm({...tourForm, date:e.target.value})}
                     className="rounded-full h-10 px-4 mt-1" data-testid="tour-date" /></div>
            <div><Label>Zuweisen an</Label>
              <Select value={tourForm.assigned_to || "__none__"} onValueChange={v=>setTourForm({...tourForm, assigned_to: v === "__none__" ? "" : v})}>
                <SelectTrigger className="rounded-full h-10 px-4 mt-1" data-testid="tour-assign"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Keine Zuweisung —</SelectItem>
                  {users.filter(u=>u.role !== "customer").map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notizen</Label>
              <Input value={tourForm.notes} onChange={e=>setTourForm({...tourForm, notes:e.target.value})}
                     className="rounded-full h-10 px-4 mt-1" data-testid="tour-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={()=>setSaveOpen(false)}>Abbrechen</Button>
            <Button onClick={saveTour} className="rounded-full" data-testid="btn-confirm-save-tour"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Preview Dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => {
        if (savingImport) return; // prevent closing during save
        setImportOpen(o);
        if (!o) { setPreview(null); setChosenRows(new Set()); }
      }}>
        <DialogContent className="rounded-3xl max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-accent" /> Import-Vorschau</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 overflow-auto">
              <div className="flex items-center justify-between flex-wrap gap-3 px-1">
                <div className="text-sm text-muted-foreground">
                  <b className="text-foreground">{preview.count}</b> Zeilen ·{" "}
                  <b className="text-foreground">{preview.rows.filter(r => r.lat && r.lng).length}</b> mit Koordinaten ·{" "}
                  <b className="text-foreground">{preview.rows.filter(r => r.geocoded).length}</b> automatisch geocodiert
                  {preview.geocode_stats && Object.keys(preview.geocode_stats).length > 0 && (
                    <span className="ml-2 inline-flex gap-1 flex-wrap">
                      {preview.geocode_stats.photon > 0 && <Badge variant="secondary" className="rounded-full text-[10px]">Photon: {preview.geocode_stats.photon}</Badge>}
                      {preview.geocode_stats.geoapify > 0 && <Badge className="rounded-full text-[10px] bg-primary/15 text-primary">Geoapify: {preview.geocode_stats.geoapify}</Badge>}
                      {preview.geocode_stats.nominatim > 0 && <Badge variant="secondary" className="rounded-full text-[10px]">Nominatim: {preview.geocode_stats.nominatim}</Badge>}
                      {preview.geocode_stats.failed > 0 && <Badge variant="destructive" className="rounded-full text-[10px]">Nicht gefunden: {preview.geocode_stats.failed}</Badge>}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={assignTo || "__none__"} onValueChange={(v)=>setAssignTo(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="rounded-full h-9 text-xs w-56" data-testid="import-assign"><SelectValue placeholder="Mitarbeiter zuweisen (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Keine Zuweisung —</SelectItem>
                      {users.filter(u => u.role !== "customer").map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="rounded-full"
                          onClick={()=>setChosenRows(new Set(preview.rows.map(r=>r.row_index)))}>Alle</Button>
                  <Button variant="outline" size="sm" className="rounded-full"
                          onClick={()=>setChosenRows(new Set())}>Keine</Button>
                </div>
              </div>

              <VirtualImportTable rows={preview.rows} chosen={chosenRows} onToggle={toggleRow} />

              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-3 flex items-start gap-3 flex-wrap" data-testid="auto-tour-box">
                <label className="flex items-center gap-2 cursor-pointer shrink-0">
                  <input type="checkbox" checked={autoTour}
                         onChange={e => setAutoTour(e.target.checked)}
                         data-testid="auto-tour-toggle" />
                  <span className="text-sm font-semibold flex items-center gap-1.5">
                    <Bookmark className="w-4 h-4 text-primary" /> Als Tour speichern
                  </span>
                </label>
                <Input
                  value={tourNameInput}
                  onChange={e => setTourNameInput(e.target.value)}
                  disabled={!autoTour}
                  placeholder="Tour-Name"
                  className="rounded-full h-9 flex-1 min-w-[200px]"
                  data-testid="auto-tour-name"
                />
                <span className="text-[11px] text-muted-foreground w-full">
                  Der hochgeladene Stapel bleibt unter &bdquo;Touren&ldquo; gespeichert und kann sp&auml;ter erneut ge&ouml;ffnet werden.
                </span>
              </div>

              <div className="text-[11px] text-muted-foreground px-1">
                Unterstützte Spalten: Titel/Name, Adresse, Lat, Lng, Datum/Termin, Dauer, Preis, Beschreibung, Kunde.
                Geocoding über <b>Photon</b> (kostenlos) → <b>Geoapify</b> (Premium-Fallback{preview.geoapify_enabled ? " ✓ aktiv" : " – nicht konfiguriert"}) → <b>Nominatim</b>.
              </div>
            </div>
          )}
          {savingImport && (
            <div className="rounded-2xl bg-primary/10 border border-primary/30 p-4 flex items-center gap-3" data-testid="save-progress">
              <div className="w-6 h-6 rounded-full border-2 border-primary/40 border-t-primary animate-spin shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Speichere Einsätze... {saveProgress.done} / {saveProgress.total}</div>
                <div className="w-full h-1.5 bg-background/60 rounded-full mt-1.5 overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300"
                       style={{ width: `${saveProgress.total > 0 ? (saveProgress.done / saveProgress.total) * 100 : 0}%` }} />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">Große Listen werden in Blöcken übertragen — bitte nicht schließen</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setImportOpen(false)}
                    disabled={savingImport}>Abbrechen</Button>
            <Button className="rounded-full" onClick={saveImport}
                    disabled={!preview || chosenRows.size === 0 || savingImport}
                    data-testid="btn-save-import">
              {savingImport
                ? (<><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin mr-2" /> Speichere...</>)
                : (<><Upload className="w-4 h-4 mr-1" /> {chosenRows.size} Einsätze erstellen</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Virtualized import table for 1000s of rows
function VirtualImportTable({ rows, chosen, onToggle }) {
  const rowH = 44;
  const ref = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const viewH = 440;
  const total = rows.length;
  const start = Math.max(0, Math.floor(scrollTop / rowH) - 5);
  const end = Math.min(total, start + Math.ceil(viewH / rowH) + 10);
  const visible = rows.slice(start, end);

  return (
    <div className="rounded-2xl border border-border/60 overflow-hidden">
      <div className="bg-secondary/60 sticky top-0 grid grid-cols-12 px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        <div className="col-span-1">✓</div>
        <div className="col-span-3">Titel</div>
        <div className="col-span-4">Adresse</div>
        <div className="col-span-2">Koordinaten</div>
        <div className="col-span-1">Termin</div>
        <div className="col-span-1 text-right">Preis</div>
      </div>
      <div ref={ref} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} style={{ maxHeight: viewH, overflow: "auto" }}>
        <div style={{ height: total * rowH, position: "relative" }}>
          <div style={{ position: "absolute", top: start * rowH, left: 0, right: 0 }}>
            {visible.map(r => (
              <div key={r.row_index} style={{ height: rowH }} className="grid grid-cols-12 px-3 items-center border-t border-border/40 hover:bg-secondary/30 text-sm">
                <div className="col-span-1">
                  <input type="checkbox" checked={chosen.has(r.row_index)} onChange={() => onToggle(r.row_index)}
                         data-testid={`import-row-${r.row_index}`} />
                </div>
                <div className="col-span-3 font-medium truncate">{r.title}</div>
                <div className="col-span-4 text-muted-foreground truncate">{r.address || "—"}</div>
                <div className="col-span-2 text-xs truncate">
                  {r.lat && r.lng
                    ? <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${r.geocoded ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>{r.lat.toFixed(3)}, {r.lng.toFixed(3)}</span>
                    : <span className="text-destructive">—</span>}
                </div>
                <div className="col-span-1 text-xs text-muted-foreground truncate">{r.scheduled_at ? new Date(r.scheduled_at).toLocaleDateString('de-DE') : "—"}</div>
                <div className="col-span-1 text-right font-mono text-xs">{r.price > 0 ? `€${r.price.toFixed(0)}` : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
